/**
 * HomeworkCard — én lekse, som en TaskRow (#154, steg 4b; Fase 4).
 *
 * Lekser har hele oppgaveløkka (poeng, ta → fullfør → godkjenning), så kortet
 * deler knappene med <ChoreCard> (taskActions), men skiller seg på to måter:
 *   1. fristen er viktig og står i metalinja («Frist i morgen», «Fristen er
 *      gått ut»), i amber når den haster (prinsipp 3 i HANDOFF.md);
 *   2. en levert lekse (`completed`) blir stående med «Godkjent» i stedet for
 *      å se utløpt ut.
 *
 * Variantene (som ChoreCard):
 *   available            → «Ta» (aldersgrense / låst til andre gjør den inaktiv)
 *   claimed-by-me        → «Fullfør», og «Din nå · holdes i N t»
 *   claimed-by-other     → «{nick} holder på» (bare lesing)
 *   awaiting-approval    → «Venter» der knappen sto
 *   done                 → «Godkjent» der knappen sto
 *
 * `showSubject` legger faget i metalinja. Tavla bruker det; /lekser grupperer
 * allerede etter fag, så der ville faget stått to ganger.
 *
 * All tekst går gjennom t('homework.*') / t('chores.*') (MED-7).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoreIcon, type TintSlot } from '../../components/primitives/ChoreIcon';
import { StatusPill } from '../../components/primitives/StatusPill';
import { TaskRow } from '../../components/primitives/TaskRow';
import type { Viewer } from './ChoreCard';
import type { HomeworkInstance } from './useHomeworkQuery';
import { describeDue, isUrgentDue } from './homeworkDue';
import {
  AWAITING_LINE,
  CompleteButton,
  HeldByMeMeta,
  MINE_LINE,
  TakeButton,
  joinMeta,
} from './taskActions';

type Variant =
  | 'available'
  | 'claimed-by-me'
  | 'claimed-by-other'
  | 'awaiting-approval-by-me'
  | 'done';

interface HomeworkCardProps {
  instance: HomeworkInstance;
  viewer: Viewer;
  onClaim?: () => void;
  onComplete?: () => void;
  spinner?: boolean;
  /** Vis faget i metalinja (tavla). Standard av: /lekser har faget som overskrift. */
  showSubject?: boolean;
}

function resolveVariant(instance: HomeworkInstance, viewer: Viewer): Variant {
  if (instance.status === 'completed') return 'done';
  if (instance.status === 'awaiting_approval') {
    return instance.claimed_by === viewer.id ? 'awaiting-approval-by-me' : 'claimed-by-other';
  }
  if (instance.status === 'claimed') {
    return instance.claimed_by === viewer.id ? 'claimed-by-me' : 'claimed-by-other';
  }
  // available (and any rejected→available bounce)
  return 'available';
}

/** Kantfargen (TaskRow `line`), som på ChoreCard. */
const LINE: Record<Variant, string | undefined> = {
  available: undefined,
  'claimed-by-me': MINE_LINE,
  'claimed-by-other': undefined,
  'awaiting-approval-by-me': AWAITING_LINE,
  done: undefined,
};

export function HomeworkCard({
  instance,
  viewer,
  onClaim,
  onComplete,
  spinner = false,
  showSubject = false,
}: HomeworkCardProps): ReactNode {
  const { t } = useTranslation();
  const variant = resolveVariant(instance, viewer);
  const ageGated = variant === 'available' && instance.age_gated;
  const assignmentLocked = variant === 'available' && !!instance.assignment_locked;
  const locked = ageGated || assignmentLocked;
  const assigneeNames = (instance.assignee_short_names ?? []).filter(Boolean);
  const tintSlot = (instance.tint_snapshot ?? 'sage') as TintSlot | string;

  // Relativ frist — ikke på leverte kort (de er ferdige).
  const due = variant === 'done' ? { kind: 'none' as const } : describeDue(instance.due_at);
  const dueText =
    due.kind === 'overdue'
      ? t('homework.overdue')
      : due.kind === 'today'
        ? t('homework.dueToday')
        : due.kind === 'tomorrow'
          ? t('homework.dueTomorrow')
          : due.kind === 'date'
            ? t('homework.due', { date: due.date })
            : null;
  const dueUrgent = isUrgentDue(due);
  const dueNode = dueText ? (
    <span
      key="due"
      data-testid="homework-card-due"
      className={dueUrgent ? 'font-bold text-warn' : undefined}
    >
      {dueText}
    </span>
  ) : null;

  const points = t('chores.points', { points: instance.point_value_snapshot });
  const subject = showSubject ? instance.subject : null;

  let meta: ReactNode;
  let action: ReactNode = null;
  switch (variant) {
    case 'available':
      // Fristen først: den er det som haster, og metalinja kuttes fra høyre.
      meta = joinMeta([
        dueNode,
        points,
        subject,
        ageGated ? t('chores.ageGatedFor', { minAge: instance.min_age_snapshot }) : null,
        assigneeNames.length > 0 ? (
          <span key="assignees" data-testid="homework-card-assignees">
            {assignmentLocked
              ? t('homework.lockedFor', { names: assigneeNames.join(', ') })
              : t('homework.forWhom', { names: assigneeNames.join(', ') })}
          </span>
        ) : null,
      ]);
      action = (
        <TakeButton testIdPrefix="homework-card" locked={locked} pending={spinner} onClaim={onClaim} />
      );
      break;
    case 'claimed-by-me':
      meta = joinMeta([<HeldByMeMeta key="held" claimExpiresAt={instance.claim_expires_at} />, dueNode]);
      action = (
        <CompleteButton testIdPrefix="homework-card" pending={spinner} onComplete={onComplete} />
      );
      break;
    case 'claimed-by-other':
      meta = joinMeta([
        points,
        <span key="taken" className="font-bold text-good">
          {t('homework.takenBy', { nick: instance.claimant_short_name ?? '' })}
        </span>,
      ]);
      break;
    case 'awaiting-approval-by-me':
      meta = joinMeta([dueNode, points, subject]);
      action = <StatusPill kind="awaiting" className="justify-center" />;
      break;
    case 'done':
      meta = joinMeta([points, subject]);
      action = <StatusPill kind="approved" className="justify-center" />;
      break;
  }

  return (
    <TaskRow
      data-testid="homework-card"
      data-variant={variant}
      aria-busy={spinner || undefined}
      line={LINE[variant]}
      icon={<ChoreIcon code={instance.icon_key ?? 'book'} tintSlot={tintSlot} size={44} />}
      title={instance.title_snapshot}
      meta={meta}
      action={action}
    />
  );
}
